import { sameDecimal } from "../index";
import type { Field } from "./dataset";
import { type DatasetKey, datasets } from "./datasets";

// What the worker and the app both need to know of the values of a record (ADR-077): which fields a dataset has,
// which other datasets it points to, and which fields differ between two versions of a record. One comparison for
// the `PATCH` the app sends and for the changes the worker writes in the audit.

/** The value of a field as a record carries it: a list for the fields that hold records of another dataset. */
export type FieldValue = string | number | boolean | string[] | null;
export type RecordValues = Record<string, FieldValue>;

/** `{ field: [old, new] }` */
export type FieldChanges = Record<string, [FieldValue, FieldValue]>;

/** The fields of a dataset by name, in the order grid and form show them. */
export const fieldsOf = (dataset: DatasetKey): [string, Field][] => Object.entries(datasets[dataset].fields);

/** The datasets the fields of a dataset point to, each once. */
export const referencedDatasets = (dataset: DatasetKey): DatasetKey[] => [
  ...new Set(fieldsOf(dataset).flatMap(([, field]) => (field.references ? [field.references as DatasetKey] : []))),
];

/** The fields that differ between two versions of a record, each with its old and its new value. */
export function changedFields(dataset: DatasetKey, before: RecordValues, after: RecordValues): FieldChanges {
  const changed = fieldsOf(dataset)
    .map(([name, field]) => [name, field, before[name] ?? null, after[name] ?? null] as const)
    .filter(([, field, old, next]) => !sameValue(field, old, next));
  return Object.fromEntries(changed.map(([name, , old, next]) => [name, [old, next]]));
}

function sameValue(field: Field, a: FieldValue, b: FieldValue): boolean {
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, i) => item === b[i]);
  // `numeric` comes back with its scale ("30.00"): the same amount is not a change
  if (field.kind === "decimal") return sameDecimal(String(a), String(b));
  return a === b;
}
